import { createFileRoute } from "@tanstack/react-router";
import * as m from "~/paraglide/messages";
import { JsonLd } from "~/components/seo/JsonLd";
import { breadcrumbSchema, webPageSchema, schemaGraph } from "~/lib/seo/schema";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  const jsonLd = schemaGraph(
    webPageSchema({
      name: "Privacy Policy — Alchema",
      description:
        "Alchema privacy policy. Learn how we collect, process, and protect your personal data in compliance with GDPR.",
      url: "/privacy",
    }),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Datenschutz", url: "/privacy" },
    ]),
  );

  return (
    <div
      className="mx-auto max-w-3xl px-6 py-16"
      style={{ color: "var(--text)" }}
    >
      <JsonLd json={jsonLd} />
      <h1
        className="text-3xl font-bold tracking-tight"
        style={{ color: "var(--text)" }}
      >
        {m.privacy_title()}
      </h1>
      <p
        className="mt-2 text-sm"
        style={{ color: "var(--text-muted)" }}
      >
        {m.privacy_last_updated()}
      </p>

      {/* 1. Data Controller */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_data_controller_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_data_controller_text()}{" "}
          <a
            href="mailto:privacy@alchema.eu"
            className="underline underline-offset-2"
            style={{ color: "var(--accent)" }}
          >
            privacy@alchema.eu
          </a>
          .
        </p>
      </section>

      {/* 2. Data Processing */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_data_processing_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_data_processing_intro()}
        </p>
        <ul
          className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <li>
            <strong>{m.privacy_data_processing_item_account()}</strong>
            {m.privacy_data_processing_item_account_desc()}
          </li>
          <li>
            <strong>{m.privacy_data_processing_item_resume()}</strong>
            {m.privacy_data_processing_item_resume_desc()}
          </li>
          <li>
            <strong>{m.privacy_data_processing_item_job()}</strong>
            {m.privacy_data_processing_item_job_desc()}
          </li>
          <li>
            <strong>{m.privacy_data_processing_item_ai()}</strong>
            {m.privacy_data_processing_item_ai_desc()}
          </li>
          <li>
            <strong>{m.privacy_data_processing_item_usage()}</strong>
            {m.privacy_data_processing_item_usage_desc()}
          </li>
        </ul>
      </section>

      {/* 3. Sub-Processors */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_sub_processors_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_sub_processors_intro()}
        </p>
        <div className="mt-4 overflow-x-auto">
          <table
            className="w-full text-sm"
            style={{ color: "var(--text-secondary)" }}
          >
            <thead>
              <tr
                className="border-b text-left"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <th className="pb-2 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  {m.privacy_table_header_sub_processor()}
                </th>
                <th className="pb-2 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  {m.privacy_table_header_purpose()}
                </th>
                <th className="pb-2 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  {m.privacy_table_header_legal_basis()}
                </th>
                <th className="pb-2 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  {m.privacy_table_header_data_categories()}
                </th>
                <th className="pb-2 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  {m.privacy_table_header_retention()}
                </th>
                <th className="pb-2 font-medium" style={{ color: "var(--text)" }}>
                  {m.privacy_table_header_location()}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Mistral AI
                </td>
                <td className="py-3 pr-4">
                  AI text generation for resume tailoring, cover letters, and
                  interview preparation
                </td>
                <td className="py-3 pr-4">
                  Contract performance (Art. 6(1)(b) GDPR), DPA
                </td>
                <td className="py-3 pr-4">Resume text, cover letter text, job descriptions</td>
                <td className="py-3 pr-4">Depends on the applicable provider configuration and terms</td>
                <td className="py-3">EU (Paris, France)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Neon
                </td>
                <td className="py-3 pr-4">Database hosting</td>
                <td className="py-3 pr-4">Contract performance</td>
                <td className="py-3 pr-4">All user data (profiles, resumes, jobs, credits)</td>
                <td className="py-3 pr-4">Until account deletion + 30 days</td>
                <td className="py-3">EU (Frankfurt, Germany)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Vercel
                </td>
                <td className="py-3 pr-4">Application hosting</td>
                <td className="py-3 pr-4">Contract performance</td>
                <td className="py-3 pr-4">Request logs, IP addresses</td>
                <td className="py-3 pr-4">30 days</td>
                <td className="py-3">EU</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  PostHog
                </td>
                <td className="py-3 pr-4">Product analytics</td>
                <td className="py-3 pr-4">Consent (Art. 6(1)(a) GDPR)</td>
                <td className="py-3 pr-4">Pseudonymous or per-event anonymous, allowlisted usage metadata</td>
                <td className="py-3 pr-4">26 months</td>
                <td className="py-3">EU (Frankfurt, Germany)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Brevo (Sendinblue SAS)
                </td>
                <td className="py-3 pr-4">Transactional and marketing email</td>
                <td className="py-3 pr-4">Contract performance, Consent for marketing</td>
                <td className="py-3 pr-4">Email addresses, names, email content</td>
                <td className="py-3 pr-4">Until unsubscribe + 30 days</td>
                <td className="py-3">EU (Paris, France)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Sentry
                </td>
                <td className="py-3 pr-4">Error tracking and monitoring</td>
                <td className="py-3 pr-4">Legitimate interest (Art. 6(1)(f) GDPR)</td>
                <td className="py-3 pr-4">Allowlisted technical error codes and environment metadata</td>
                <td className="py-3 pr-4">90 days</td>
                <td className="py-3">EU</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Strato AG
                </td>
                <td className="py-3 pr-4">Domain registration and DNS</td>
                <td className="py-3 pr-4">Legitimate interest (Art. 6(1)(f) GDPR)</td>
                <td className="py-3 pr-4">Domain registration data</td>
                <td className="py-3 pr-4">Duration of domain registration</td>
                <td className="py-3">EU (Berlin, Germany)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Hetzner Online GmbH
                </td>
                <td className="py-3 pr-4">Infrastructure hosting (automation and PDF services)</td>
                <td className="py-3 pr-4">Contract performance</td>
                <td className="py-3 pr-4">Server logs, application data</td>
                <td className="py-3 pr-4">7 days (logs)</td>
                <td className="py-3">EU (Nuremberg, Germany)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Stripe, Inc.
                </td>
                <td className="py-3 pr-4">Payment processing and subscription management</td>
                <td className="py-3 pr-4">Contract performance (Art. 6(1)(b) GDPR)</td>
                <td className="py-3 pr-4">Payment details, billing address, email</td>
                <td className="py-3 pr-4">10 years (German tax law GoBD)</td>
                <td className="py-3">EU (Dublin, Ireland)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Google Ireland Limited
                </td>
                <td className="py-3 pr-4">Authentication (Google OAuth)</td>
                <td className="py-3 pr-4">Contract performance (Art. 6(1)(b) GDPR)</td>
                <td className="py-3 pr-4">Email address, name, profile picture (OAuth)</td>
                <td className="py-3 pr-4">Until account deletion</td>
                <td className="py-3">EU (Dublin, Ireland)</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Apify
                </td>
                <td className="py-3 pr-4">Job listing data collection and aggregation</td>
                <td className="py-3 pr-4">Legitimate interest (Art. 6(1)(f) GDPR)</td>
                <td className="py-3 pr-4">Publicly available job listings</td>
                <td className="py-3 pr-4">Processed and deleted within 24 hours</td>
                <td className="py-3">EU</td>
              </tr>
              <tr
                className="border-b"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-3 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  Cloudflare
                </td>
                <td className="py-3 pr-4">DNS management, CDN, and email routing</td>
                <td className="py-3 pr-4">Legitimate interest (Art. 6(1)(f) GDPR)</td>
                <td className="py-3 pr-4">IP addresses, DNS queries</td>
                <td className="py-3 pr-4">DNS logs 24 hours</td>
                <td className="py-3">Global (EU processing)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 4. Your Rights */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_rights_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_rights_intro()}
        </p>
        <ul
          className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <li>
            <strong>{m.privacy_rights_item_access()}</strong>
            {m.privacy_rights_item_access_desc()}
          </li>
          <li>
            <strong>{m.privacy_rights_item_rectification()}</strong>
            {m.privacy_rights_item_rectification_desc()}
          </li>
          <li>
            <strong>{m.privacy_rights_item_erasure()}</strong>
            {m.privacy_rights_item_erasure_desc()}
          </li>
          <li>
            <strong>{m.privacy_rights_item_portability()}</strong>
            {m.privacy_rights_item_portability_desc()}
          </li>
          <li>
            <strong>{m.privacy_rights_item_object()}</strong>
            {m.privacy_rights_item_object_desc()}
          </li>
          <li>
            <strong>{m.privacy_rights_item_restrict()}</strong>
            {m.privacy_rights_item_restrict_desc()}
          </li>
          <li>
            <strong>{m.privacy_rights_item_withdraw()}</strong>
            {m.privacy_rights_item_withdraw_desc()}
          </li>
        </ul>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_rights_outro()}{" "}
          <a
            href="mailto:privacy@alchema.eu"
            className="underline underline-offset-2"
            style={{ color: "var(--accent)" }}
          >
            privacy@alchema.eu
          </a>
          {m.privacy_rights_outro_suffix()}
        </p>
      </section>

      {/* 5. Data Retention */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_data_retention_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_data_retention_text()}
        </p>
      </section>

      {/* 6. Contact */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_contact_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_contact_intro()}
        </p>
        <address
          className="mt-3 text-sm not-italic leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_org_name()}
          <br />
          {m.privacy_org_dpo()}
          <br />
          <a
            href="mailto:privacy@alchema.eu"
            className="underline underline-offset-2"
            style={{ color: "var(--accent)" }}
          >
            privacy@alchema.eu
          </a>
        </address>
      </section>

      {/* 7. Supervisory Authority */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_supervisory_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_supervisory_text()}
        </p>
        <p
          className="mt-2 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_supervisory_authority()}
          <br />
          <a
            href={m.privacy_supervisory_url()}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
            style={{ color: "var(--accent)" }}
          >
            {m.privacy_supervisory_url()}
          </a>
        </p>
      </section>

      {/* 8. Cookies and Similar Technologies */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_cookies_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_cookies_text()}
        </p>
        <ul
          className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <li>
            <strong>{m.privacy_cookies_essential()}</strong>
            {m.privacy_cookies_essential_desc()}
          </li>
          <li>
            <strong>{m.privacy_cookies_analytics()}</strong>
            {m.privacy_cookies_analytics_desc()}
          </li>
          <li>
            <strong>{m.privacy_cookies_error()}</strong>
            {m.privacy_cookies_error_desc()}
          </li>
        </ul>
      </section>

      {/* 9. Automated Decision-Making and AI Processing */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_automated_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_automated_text()}
        </p>
      </section>

      {/* 10. Data Breach Notification */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_breach_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_breach_text()}{" "}
          <a
            href="mailto:privacy@alchema.eu"
            className="underline underline-offset-2"
            style={{ color: "var(--accent)" }}
          >
            privacy@alchema.eu
          </a>
          .
        </p>
      </section>

      {/* 11. Children */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_children_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_children_text()}
        </p>
      </section>

      {/* 12. Data Minimization */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.privacy_minimization_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_minimization_text()}
        </p>
      </section>

      {/* 13. AI Processing and EU AI Act */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          13. {m.privacy_ai_processing_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.privacy_ai_processing_text()}
        </p>
      </section>
    </div>
  );
}
